import { useCallback, useState } from 'react';
import { useApiClient } from '../../../utils/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import type { TeamStatusScope, TeamStatusTodayCounts } from '../types';

export interface UseTeamStatusScopeResult {
  /** The counts to render — the toggled scope's, or the summary's. */
  counts: TeamStatusTodayCounts | undefined;
  scope: TeamStatusScope;
  switching: boolean;
  error: string | null;
  setScope: (next: TeamStatusScope) => void;
}

/**
 * The team-status widget's scope toggle.
 *
 * Reads its own endpoint rather than re-fetching the dashboard summary. The
 * summary is served through a URL-keyed deduping cache with no notion of query
 * parameters, and reloading every widget on the page to flip one control would
 * make a cheap toggle feel expensive — and would blank the widgets beside it.
 *
 * The summary's payload is still the first paint: `override` starts null and the
 * caller's `fromSummary` is shown until somebody actually switches.
 */
export function useTeamStatusScope(
  fromSummary: TeamStatusTodayCounts | undefined,
): UseTeamStatusScopeResult {
  const { getAccessToken } = useAuth();
  const apiClient = useApiClient(getAccessToken);
  const [override, setOverride] = useState<TeamStatusTodayCounts | null>(null);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const counts = override ?? fromSummary;

  const setScope = useCallback(
    (next: TeamStatusScope) => {
      if (next === (counts?.scope ?? 'mine')) return;
      setSwitching(true);
      setError(null);
      void apiClient
        .get<TeamStatusTodayCounts | null>(`/api/dashboard/team-status?scope=${next}`)
        .then((result) => {
          // A 204 arrives as null: the caller can see nobody at that scope. The
          // previous counts stay on screen rather than the widget emptying,
          // because "you cannot see this" is not "there is nothing here".
          if (result) setOverride(result);
          else setError('You do not have access to that view.');
        })
        .catch(() => setError('Could not switch the view. Try again.'))
        .finally(() => setSwitching(false));
    },
    [apiClient, counts?.scope],
  );

  return {
    counts,
    scope: counts?.scope ?? 'mine',
    switching,
    error,
    setScope,
  };
}
